import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { clone } from 'lodash';
import { BehaviorSubject } from 'rxjs';

import { IEventType } from '../models/events-data';
import { IPlotRange } from '../models/range';

import { Latis } from './latis.service';

interface IEventObject {
    url: string;
    types: any[];
    events: any[];
    loading: boolean;
}

// TODO: Get this from constants file
const defaultEvents = [ 'Shadow', 'StationContact', 'Region of Interest', 'Science Region of Interest' ];

@Injectable({ providedIn: 'root' })
export class EventsService {
    eventsMap: Map<string, BehaviorSubject<IEventObject>> = new Map();
    allEventTypes: BehaviorSubject<IEventType[]> = new BehaviorSubject([]);
    // TODO: create a LRU data cache

    constructor( private _latis: Latis, private _http: HttpClient ) {}

    /** add a new entry for a plot's datasets to the map */
    initEventsForPlot( plotId: string, eventsUrl: string ): void {
        this.eventsMap.set( plotId, new BehaviorSubject({
            url: eventsUrl,
            types: [],
            events: [],
            loading: true
        }));
    }

    /** return the observable that updates whenever we update this plots datasets */
    getPlotEvents( plotId: string ): IEventObject {
        return this.eventsMap.get( plotId ).getValue();
    }

    /** return the observable that updates whenever we update this plots datasets */
    getPlotEvents$( plotId: string ): BehaviorSubject<IEventObject> {
        return this.eventsMap.get( plotId );
    }

    /** download events data from the server, updates our events observable when complete */
    downloadEventsData( plotId: string, range: IPlotRange ): void {
        const accessURL = this.getPlotEvents( plotId ).url;
        let types;
        let events;

        const url = accessURL + '&time>=' + new Date( range.start ).toISOString() + '&time<=' + new Date( range.end ).toISOString();

        this._http.get( this._latis.latisBase + url ).subscribe( ( response: any ) => {
            // parse data
            // find indexes of parameters based on metadata
            const parameters = response.Events.parameters;
            const idIndex = parameters.indexOf( 'id' );
            const typeIdIndex = parameters.indexOf( 'typeId' );
            const startIndex = parameters.indexOf( 'startTime' );
            const endIndex = parameters.indexOf( 'endTime' );

            // Get the list of unique event types returned by the metadata.
            // This should be a list of all possible event types.
            types = response.Events.metadata.typeId.event_types;
            types.map( type => {
                const isDefaultEvent = defaultEvents.includes( type.name );
                type.checked = isDefaultEvent;
            });

            // now build a list of events
            events = response.Events.data.map( event => {
                const eventTypeIndex = types.findIndex( type => event[typeIdIndex] === type.id );
                return {
                    type: types[eventTypeIndex],
                    y: eventTypeIndex,
                    start: event[startIndex],
                    end: event[endIndex],
                    info: {
                        'Event ID': event[idIndex].toString()
                    }
                };
            });

            this.updateAllEventTypes( types );
            this.getPlotEvents$( plotId ).next({
                url: accessURL,
                types: types,
                events: events,
                loading: false
            });
        });
    }

    /** take a list of event types and add new/unique types to the global list */
    updateAllEventTypes( newEventTypes: IEventType[] ): void {
        const allCurrentEventTypes = this.allEventTypes.getValue();
        newEventTypes.forEach( newEvent => {
            if ( !allCurrentEventTypes.some( currentEvent => newEvent.name === currentEvent.name ) ) {
                allCurrentEventTypes.push( clone( newEvent ) );
            }
        });
        this.allEventTypes.next( allCurrentEventTypes );
    }

    /** return an observable that updates when we update the list of all event types */
    getAllEventTypes$(): BehaviorSubject<IEventType[]> {
        return this.allEventTypes;
    }
}
